// The ballots open to a member of Synod — the list on their landing, and the
// only way into the cast screen.
//
// ── IT CARRIES NO FIGURE, LIKE EVERYTHING ELSE A MEMBER SEES ───────────────
// No turnout, no count of how many have voted, no closing time and no
// countdown. `GET /api/me/ballots` returns none, so there is none here to leak:
// a screen that cannot say a number cannot be made to say one by the next person
// in a hurry. That is the same construction the landing itself uses, and the
// same registrar ruling (2026-08-16).
//
// ── A LINK IS NOT AN AUTHORISATION ─────────────────────────────────────────
// Reaching `#ballot/<id>` gets you the screen. Getting a PAPER on it takes
// standing on the frozen roll for that sitting, re-resolved server-side on the
// request that asks — so a member who types somebody else's ballot id is
// refused, and a member whose standing was withdrawn between two loads is
// refused on the second. Showing the link is courtesy; hiding it is not control.
//
// ── EMPTY IS A SENTENCE, NOT A BLANK ───────────────────────────────────────
// "No ballot is open for you" is the ordinary state of this list for most of a
// sitting. It is said as a fact, with what happens next, because a member
// looking at an empty page during a vote cannot tell that from a broken one.

import { useCallback, useEffect, useState, type JSX } from 'react';
import { api, DecidesApiError, type OpenBallot } from '../api/client';

/** A rehearsal or sandbox ballot must never be mistaken for the real thing. The
 *  same three words the landing and the results sheet use, one screen earlier —
 *  where the choice to open a paper is actually made. */
const MODE_NOTE: Record<string, string> = {
  rehearsal: 'Rehearsal — not a live vote. Nothing you mark here binds Synod.',
  sandbox: 'Sandbox — practice data. Nothing you mark here binds Synod.',
};

type State =
  | { status: 'loading' }
  | { status: 'ready'; ballots: OpenBallot[] }
  | { status: 'failed'; code: string };

export function BallotList(): JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    setState({ status: 'loading' });
    try {
      const answer = await api.myBallots();
      setState({ status: 'ready', ballots: answer.ballots });
    } catch (err) {
      setState({
        status: 'failed',
        code: err instanceof DecidesApiError ? err.code : 'unreachable',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === 'loading') {
    return <p role="status">Looking for ballots open to you…</p>;
  }

  if (state.status === 'failed') {
    // THE FACT FIRST, THEN THE BUTTON. A member who cannot see their ballots
    // needs to know that nothing has happened to their vote before they are
    // invited to jab at anything.
    return (
      <>
        <p className="alert">
          The list of open ballots could not be read. No paper of yours has been recorded or lost —
          this is a problem reaching the server, not a problem with your vote.
        </p>
        <button type="button" className="btn btn-ghost" onClick={() => void load()}>
          Try again
        </button>
      </>
    );
  }

  if (state.ballots.length === 0) {
    return (
      <p>
        No ballot is open to you at the moment. When the President opens one, it appears here — this
        page does not refresh itself, so look again when you are told voting is open.
      </p>
    );
  }

  return (
    <ul>
      {state.ballots.map((ballot) => (
        <li key={ballot.ballotId}>
          <a href={`#ballot/${encodeURIComponent(ballot.ballotId)}`}>{ballot.title}</a>
          <br />
          {/* The SITTING's name — `election.name`, which the route sends as
              `electionName`. This line read `ballot.sittingName` until the
              contract was reconciled on 2026-08-27: a field the route does not
              send renders as nothing at all, so a member on two rolls would
              have been shown two identically-labelled ballots with no way to
              tell which sitting either belonged to. */}
          {ballot.electionName}
          {MODE_NOTE[ballot.mode] !== undefined && (
            <>
              <br />
              <em>{MODE_NOTE[ballot.mode]}</em>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
